import CategoryDescription from 'components/CategoryDescription';
import { DATA_SOURCES } from '@/config/constants';
import { exportEntity, hasFlavorsTab } from '../shared/entityPredicates';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import { EntityInsightBar } from 'components/AiAssistant';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import { LifeCycleRow } from 'components/LifeCycleBadge';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import PopularityBreadCrumbs from 'components/Breadcrumbs/PopularityBreadCrumbs';
import { PopularityChartCard } from '../shared/entity/chartCards';
import PopularityIndexRelatedEntitiesSectionTable from 'components/DataVis/PopularityIndexRelatedEntitiesTable/PopularityIndexRelatedEntitiesSectionTable';
import ToggleWatch from 'components/Button/ToggleWatch';
import { useCurrentCountry } from 'utils/hooks';
import usePopIndexEntity from '../shared/entity/usePopIndexEntity';
import { useTrackCategoryView } from 'utils/hooks/useTrackEntityView';

const METRICS = [
    { key: 'popularity_share', healthStatKey: 'popularity share' },
    POPULARITY_METADATA_BY_KEY.convergence_prc,
];

function PopularityCategoryFivePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = usePopIndexEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hint',
    });
    const { details, name, row } = page;

    const healthStats = row?.healthStats;

    useTrackCategoryView(DATA_SOURCES.POPULARITY, {
        id: categoryId,
        categoryName: name,
        countryName: country?.name || null,
    });

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={PopularityBreadCrumbs} />}
            sourceTabs={tabs}
            title={{
                name,
                isLoading: page.isLoading || page.entityQuery.isLoading,
            }}
            actions={
                <ToggleWatch
                    id={categoryId}
                    data={{ name, type: 'category' }}
                />
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="related categories">
                        <EntityRelatedTable
                            as={PopularityIndexRelatedEntitiesSectionTable}
                            subject="categories"
                            icon="category"
                            title="Related Categories"
                            exportType="related-category"
                            exportEntity={exportEntity(page)}
                            sortParamKey="categoriesSort"
                            paginateBy="categories"
                            parented
                        />
                    </EntityTab>
                    <EntityTab name="brands">
                        <EntityRelatedTable
                            as={PopularityIndexRelatedEntitiesSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Brands"
                            exportType="brand"
                            exportEntity={exportEntity(page)}
                            sortParamKey="brandsSort"
                            paginateBy="brands"
                            parented
                            showAddToWatchlist={false}
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="pop_index"
                                exportEntity={exportEntity(page)}
                                parented
                                tooltipKey="flavorsTitleHelpPopIndex"
                            />
                        </EntityTab>
                    )}
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard healthStats={healthStats} valueTextSize="base">
                    {row && (
                        <div className="p-4 border-b border-border">
                            <LifeCycleRow data={row} />
                        </div>
                    )}
                </EntityHeroCard>
                <CategoryDescription
                    categoryId={categoryId}
                    categoryName={name}
                    flavors={details?.flavors}
                />
                <PopularityChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                    downloadEntity={exportEntity(page)}
                    chart={{ categoryDepth: details?.depth }}
                />
                <EntityInsightBar
                    entityId={categoryId}
                    entityType="category"
                    entityName={name}
                    country={country?.text_id}
                    week={page.week ? String(page.week) : undefined}
                />
            </div>
        </EntityPage>
    );
}

PopularityCategoryFivePage.displayName = 'PopularityCategoryFive';

export default PopularityCategoryFivePage;
